import React, { useState, useEffect } from "react";
import {
  IconButton,
  Drawer,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  CircularProgress as MuiCircularProgress,
  List,
  Divider,
} from "@mui/material";
import Avatar from "components/@extended/Avatar";
import {
  X as CloseIcon,
  Heart,
  UserRound,
  BookOpen,
  Award,
  Bell,
  Settings,
  LifeBuoy,
  Shield,
  LogOut,
  MailPlus,
  Layout,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel } from "utils/adminAccess";
import axios from "utils/axios";
import icons from "assets/icons";
import { useGetUser } from "api/user";
import { brand } from "brand/theme";
import { V2 } from "layout/Dashboard/Header/headerV2Tokens";

const menuRowSx = {
  py: 1,
  px: 1.25,
  borderRadius: 'var(--brand-radius-md)',
  mb: 0.35,
  color: V2.ink,
  fontFamily: V2.fontBody,
  "&:hover": { bgcolor: V2.accentSoft },
};

const menuIconSx = {
  minWidth: 0,
  mr: 1.25,
  width: 34,
  height: 34,
  borderRadius: 'var(--brand-radius-md)',
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: V2.accentSoft,
  color: V2.accent,
};

const quickCardSx = {
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  p: 2,
  borderRadius: 'var(--brand-radius-md)',
  bgcolor: V2.surfaceSolid,
  border: `1px solid ${V2.line}`,
  transition: "background 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    borderColor: V2.accentBorder,
    bgcolor: V2.accentSoft,
  },
};

const MobileHeaderMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const { user: userData } = useGetUser();
  const navigate = useNavigate();
  const isAdmin = canAccessAdminPanel(user);

  const open = Boolean(anchorEl);

  useEffect(() => {
    fetchGlobalPoints();
  }, []);

  const fetchGlobalPoints = async () => {
    try {
      const response = await axios.get("/api/gamification/dashboard-data");
      if (response.data?.global) {
        setGlobalData(response.data.global);
      }
    } catch (err) {
      console.error("Error fetching global points:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateTo = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error(err);
    }
  };

  /** Match desktop NavUser / veille card avatar fallback */
  function stringAvatar(firstName, lastName) {
    const letters =
      firstName && lastName
        ? `${firstName[0]}${lastName[0]}`.toUpperCase()
        : firstName?.[0]?.toUpperCase() || "?";

    return {
      sx: {
        bgcolor: "var(--v2-surface-2, #F0F0F2)",
        color: V2.muted,
        fontWeight: 600,
        fontSize: 11,
        fontFamily: V2.fontBody,
        border: `1px solid ${V2.line}`,
      },
      children: letters,
    };
  }

  const standardNavCards = [
    { title: "Experts", icon: icons.expert, path: "/experts" },
    { title: "Institutions", icon: icons.institutions, path: "/institutions" },
    { title: "Newsletters", icon: icons.news, path: "/newsletter" },
    { title: "Formations", icon: icons.formation, path: "/formation" },
  ];

  const adminNavCards = [
    {
      title: "Abonné",
      IconComponent: MailPlus,
      path: "/admin/newsletters",
    },
    {
      title: "Bannières publicitaires",
      IconComponent: Layout,
      path: "/admin/list-banners",
    },
  ];

  const dashboardItems = [
    { title: "Mon profil", icon: UserRound, path: "/profile/account/basic" },
    { title: "Mon apprentissage", icon: BookOpen, path: "/apprentissage" },
    { title: "Mes Certificats", icon: Award, path: "/certificats" },
    { title: "Mes favoris", icon: Heart, path: "/favorites" },
    { title: "Mes newsletters", icon: Bell, path: "/newsletter" },
  ];

  const { points, progress } = globalData || {};
  const progressPercentage = progress?.progress_percentage || 0;

  return (
    <>
      <IconButton
        onClick={handleClick}
        aria-label="Menu profil"
        sx={{
          p: 0.5,
          border: "none",
          borderRadius: 'var(--brand-radius-md)',
          bgcolor: "transparent",
          width: 40,
          height: 40,
          "&:hover": {
            bgcolor: V2.accentSoft,
          },
        }}
      >
        <Avatar
          {...stringAvatar(userData?.first_name, userData?.last_name)}
          src={userData?.profile_image_url}
          sx={{
            width: 34,
            height: 34,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: V2.fontBody,
            bgcolor: "var(--v2-surface-2, #F0F0F2)",
            color: V2.muted,
            border: `1px solid ${V2.line}`,
          }}
        />
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100vw",
            bgcolor: V2.bg,
            fontFamily: V2.fontBody,
            borderRadius: 'var(--brand-radius-md)',
            borderLeft: `1px solid ${V2.line}`,
            boxShadow: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            bgcolor: V2.surfaceSolid,
            borderBottom: `1px solid ${V2.line}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: V2.fontDisplay,
              fontWeight: 600,
              color: V2.ink,
              fontSize: "1.1rem",
              letterSpacing: "-0.02em",
            }}
          >
            Menu
          </Typography>
          <IconButton
            onClick={handleClose}
            size="large"
            aria-label="Fermer"
            sx={{
              color: V2.ink,
              borderRadius: 'var(--brand-radius-md)',
              border: `1px solid ${V2.line}`,
              width: 40,
              height: 40,
              "&:hover": {
                bgcolor: V2.accentSoft,
                color: V2.accent,
                borderColor: V2.accentBorder,
              },
            }}
          >
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: "auto", height: "100%", bgcolor: V2.bg }}>
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: V2.surfaceSolid,
              borderBottom: `1px solid ${V2.line}`,
            }}
          >
            <Avatar
              {...stringAvatar(userData?.first_name, userData?.last_name)}
              src={userData?.profile_image_url}
              sx={{
                width: 48,
                height: 48,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: V2.fontBody,
                bgcolor: "var(--v2-surface-2, #F0F0F2)",
                color: V2.muted,
                border: `1px solid ${V2.line}`,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: V2.fontDisplay,
                  fontWeight: 600,
                  color: V2.ink,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                }}
              >
                {userData?.first_name} {userData?.last_name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: V2.fontBody,
                  color: V2.muted,
                  fontSize: "0.85rem",
                  mt: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userData?.email}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: V2.surfaceSolid,
              p: 2,
              borderBottom: `1px solid ${V2.line}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: V2.fontDisplay,
                fontWeight: 600,
                fontSize: "1.15rem",
                letterSpacing: "-0.02em",
                color: V2.ink,
                mb: 1.25,
              }}
            >
              Mon Tableau de Bord
            </Typography>

            <List component="div" disablePadding>
              {dashboardItems.map((item) => {
                const Icon = item.icon;
                return (
                  <MenuItem
                    key={item.title}
                    onClick={() => navigateTo(item.path)}
                    sx={menuRowSx}
                  >
                    <ListItemIcon sx={menuIconSx}>
                      <Icon size={17} strokeWidth={2.1} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontFamily: V2.fontBody,
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    />
                  </MenuItem>
                );
              })}
            </List>

            <Divider sx={{ my: 1.25, borderColor: V2.line }} />

            <MenuItem
              onClick={() => navigateTo("/profile/setting/my-account")}
              sx={menuRowSx}
            >
              <ListItemIcon sx={menuIconSx}>
                <Settings size={17} strokeWidth={2.1} />
              </ListItemIcon>
              <ListItemText
                primary="Paramètres"
                primaryTypographyProps={{
                  fontFamily: V2.fontBody,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              />
            </MenuItem>

            <MenuItem
              onClick={() => {
                const landingUrl =
                  import.meta.env.VITE_LANDING_URL ||
                  `${window.location.protocol}//${window.location.hostname}`;
                const locale = localStorage.getItem("i18nextLng") || "fr";
                window.location.href = `${landingUrl}/${locale}/assistance`;
              }}
              sx={menuRowSx}
            >
              <ListItemIcon sx={menuIconSx}>
                <LifeBuoy size={17} strokeWidth={2.1} />
              </ListItemIcon>
              <ListItemText
                primary="Support"
                primaryTypographyProps={{
                  fontFamily: V2.fontBody,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              />
            </MenuItem>

            {isAdmin && (
              <MenuItem
                onClick={() => navigateTo("/admin/dashboard")}
                sx={menuRowSx}
              >
                <ListItemIcon sx={menuIconSx}>
                  <Shield size={17} strokeWidth={2.1} />
                </ListItemIcon>
                <ListItemText
                  primary="Admin"
                  primaryTypographyProps={{
                    fontFamily: V2.fontBody,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>
            )}
          </Box>

          <Box
            sx={{
              bgcolor: V2.surfaceSolid,
              p: 2,
              mt: 1,
              borderTop: `1px solid ${V2.line}`,
              borderBottom: `1px solid ${V2.line}`,
            }}
          >
            <Typography
              sx={{
                mb: 1.5,
                fontFamily: V2.fontBody,
                fontWeight: 600,
                color: V2.muted,
                fontSize: "0.75rem",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Accès rapide
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1,
              }}
            >
              {standardNavCards.map((card) => (
                <Box
                  key={card.title}
                  onClick={() => navigateTo(card.path)}
                  sx={quickCardSx}
                >
                  <Box
                    component="img"
                    src={card.icon}
                    alt=""
                    sx={{
                      width: 28,
                      height: 28,
                      mb: 1,
                      filter: brand.iconFilters.accent,
                    }}
                  />
                  <Typography
                    align="center"
                    sx={{
                      fontFamily: V2.fontBody,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: V2.ink,
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>
              ))}

              {isAdmin &&
                adminNavCards.map((card) => (
                  <Box
                    key={card.title}
                    onClick={() => navigateTo(card.path)}
                    sx={quickCardSx}
                  >
                    <card.IconComponent
                      size={28}
                      color={V2.accent}
                      style={{ marginBottom: 8 }}
                    />
                    <Typography
                      align="center"
                      sx={{
                        fontFamily: V2.fontBody,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: V2.ink,
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>

          {!loading && globalData && (
            <Box
              sx={{
                bgcolor: V2.surfaceSolid,
                mt: 1,
                borderTop: `1px solid ${V2.line}`,
                borderBottom: `1px solid ${V2.line}`,
              }}
            >
              <MenuItem
                onClick={() => navigateTo("/profile/account/dashboard")}
                sx={{ ...menuRowSx, py: 2, mx: 1, my: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <MuiCircularProgress
                      variant="determinate"
                      value={100}
                      size={60}
                      thickness={4}
                      sx={{
                        color: V2.accentSoft,
                        position: "absolute",
                        left: 0,
                        top: 0,
                      }}
                    />
                    <MuiCircularProgress
                      variant="determinate"
                      value={progressPercentage}
                      size={60}
                      thickness={4}
                      sx={{
                        color: V2.accent,
                        "& .MuiCircularProgress-circle": {
                          strokeLinecap: "round",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: V2.fontDisplay,
                          fontWeight: 700,
                          color: V2.ink,
                        }}
                      >
                        {points}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: V2.fontBody,
                        fontWeight: 600,
                        color: V2.ink,
                      }}
                    >
                      Points Globaux
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: V2.fontBody,
                        color: V2.muted,
                        fontSize: "0.85rem",
                      }}
                    >
                      {progressPercentage}% progression
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Box>
          )}

          <Box
            sx={{
              bgcolor: V2.surfaceSolid,
              mt: 1,
              mb: 2,
              p: 2,
              borderTop: `1px solid ${V2.line}`,
              borderBottom: `1px solid ${V2.line}`,
            }}
          >
            <MenuItem
              onClick={handleLogout}
              sx={{
                ...menuRowSx,
                color: brand.colors.critical,
                "&:hover": { bgcolor: brand.colors.criticalSoft },
              }}
            >
              <ListItemIcon
                sx={{
                  ...menuIconSx,
                  bgcolor: brand.colors.criticalSoft,
                  color: brand.colors.critical,
                }}
              >
                <LogOut size={17} strokeWidth={2.1} />
              </ListItemIcon>
              <ListItemText
                primary="Se déconnecter"
                primaryTypographyProps={{
                  fontFamily: V2.fontBody,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              />
            </MenuItem>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileHeaderMenu;
